import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_email: user?.email || "",
    shipping_phone: "",
    shipping_address: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const placeOrder = async () => {
    if (!form.shipping_name || !form.shipping_address || !form.shipping_phone || !form.shipping_email) {
      toast.error("Please fill all shipping details");
      return;
    }
    setSubmitting(true);
    try {
      let savedCoupon = null;
      try {
        savedCoupon = JSON.parse(localStorage.getItem("kk_coupon") || "null");
      } catch {}
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
        coupon_code: savedCoupon?.code || null,
        ...form,
      });

      if (!data.configured) {
        toast.success("Order placed (test mode). Add Razorpay keys to enable real payment.");
        clear();
        localStorage.removeItem("kk_coupon");
        navigate("/account");
        return;
      }

      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "konokangen",
        description: "Fine jewellery purchase",
        prefill: {
          name: form.shipping_name,
          email: form.shipping_email,
          contact: form.shipping_phone,
        },
        theme: { color: "#c58b76" },
        handler: async (res) => {
          try {
            await api.post("/orders/verify", {
              order_id: data.order.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment received. Thank you.");
            clear();
            localStorage.removeItem("kk_coupon");
            navigate("/account");
          } catch {
            toast.error("Payment verification failed");
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="text-center py-32">
        <p className="text-[#5c5a56]">Your bag is empty.</p>
      </main>
    );
  }

  return (
    <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline">Checkout</div>
      <h1 className="font-serif text-5xl mt-3 mb-10">Finish your order</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <section className="space-y-5">
          <h2 className="font-serif text-2xl mb-2">Shipping details</h2>
          {[
            ["shipping_name", "Full name"],
            ["shipping_email", "Email"],
            ["shipping_phone", "Phone"],
            ["shipping_address", "Address"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5c5a56]">{label}</label>
              {k === "shipping_address" ? (
                <textarea
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full mt-1 border border-[#ebe5df] p-3 focus:outline-none focus:border-[#c58b76] bg-white"
                  rows={3}
                  data-testid={`checkout-${k}`}
                />
              ) : (
                <input
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full mt-1 border border-[#ebe5df] p-3 focus:outline-none focus:border-[#c58b76] bg-white"
                  data-testid={`checkout-${k}`}
                />
              )}
            </div>
          ))}
        </section>
        <aside className="bg-[#f3efea] p-8 h-fit">
          <h3 className="font-serif text-2xl mb-6">Order summary</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span>
                  {it.name} <span className="text-[#5c5a56]">×{it.quantity}</span>
                </span>
                <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#ebe5df] mt-6 pt-4 flex justify-between font-serif text-2xl">
            <span>Total</span>
            <span>₹{total.toLocaleString("en-IN")}</span>
          </div>
          <button
            onClick={placeOrder}
            disabled={submitting}
            className="btn-primary w-full justify-center mt-6 disabled:opacity-60"
            data-testid="place-order-btn"
          >
            {submitting ? "Processing…" : "Pay with Razorpay"}
          </button>
        </aside>
      </div>
    </main>
  );
}
